import { Separator } from "@/components/ui/separator"
import { useProjectMeta } from '@/hooks/meta/useProjectMeta'
import { useCategoryQuery } from '@/hooks/useCategoryQueries'
import { useTemplateWithFieldsQuery } from '@/hooks/useTemplateQueries'
import { cn } from "@/lib/utils"
import { useEntity } from '@/services/query/entities-queries'
import { useFolderStructure } from '@/services/query/notes-queries'
import { findFolderInTree } from '@/services/supabase/notes'
import { ChevronRight, Home } from "lucide-react"
import * as React from "react"
import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

export function PageTitle() {
    const location = useLocation();
    const { projectId, entityId, categoryId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [projectName, setProjectName] = useState<string>('');
    const [entityName, setEntityName] = useState<string>('');
    const [categoryName, setCategoryName] = useState<string>('');

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    const { name: storeProjectName } = useProjectMeta(projectId || null);

    const { data: categoryData } = useCategoryQuery(categoryId);
    const { data: entityData } = useEntity(entityId, { enabled: !!entityId });

    const templateIdParam = searchParams.get('template');
    const templateId = templateIdParam ? parseInt(templateIdParam) : null;
    const { data: templateData } = useTemplateWithFieldsQuery(templateId);

    const shouldFetchFolders = lastSegment === 'notes';
    const { data: folderStructure = [] } = useFolderStructure(projectId, { enabled: shouldFetchFolders });
    const currentFolderId = searchParams.get('folder') ? parseInt(searchParams.get('folder')!) : null;

    const { breadcrumbs: folderBreadcrumbs } = React.useMemo(() => {
        if (currentFolderId && folderStructure.length > 0) {
            const result = findFolderInTree(folderStructure, currentFolderId);
            if (result) {
                return { breadcrumbs: result.path };
            }
        }
        return { breadcrumbs: [] };
    }, [folderStructure, currentFolderId]);

    React.useEffect(() => {
        if (entityData?.name) setEntityName(entityData.name);
    }, [entityData]);

    React.useEffect(() => { if (storeProjectName) setProjectName(storeProjectName); }, [storeProjectName]);
    React.useEffect(() => {
        if (categoryData) setCategoryName(categoryData.name);
        else setCategoryName('');
    }, [categoryData]);

    const sidebarItems: Record<string, string> = {
        'overview': 'Overview',
        'timeline': 'Timeline',
        'chapters': 'Manuscript',
        'characters': 'Characters',
        'locations': 'Locations',
        'items': 'Items',
        'tags': 'Tags',
        'settings': 'Settings',
        'progress': 'Progress Tracker',
        'form-builder': 'Plot Elements',
        'mind-maps': 'Mind Maps',
        'elements': 'Elements',
        'templates': 'Templates',
        'ai-prompts': 'Prompts',
        'notes': 'Notes',
        'thoughts': 'Thoughts'
    };

    let title = "Cognito";

    if (pathSegments.includes('entities') && entityName) {
        title = entityName;
    } else if (pathSegments.includes('elements') && categoryName && categoryId) {
        title = categoryName;
    } else if (pathSegments.includes('form-builder') && templateData?.name) {
        title = templateData.name;
    } else if (pathSegments.includes('chapters') && pathSegments.length > 3) {
        title = "Chapter";
    } else if (sidebarItems[lastSegment]) {
        title = sidebarItems[lastSegment];
    } else {
        title = lastSegment ? (lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' ')) : (projectName || "Home");
    }

    const showElementsBreadcrumbs = (pathSegments.includes('elements') && Boolean(categoryId)) || (pathSegments.includes('entities') && Boolean(entityId));

    const breadcrumbCategoryId = categoryId || entityData?.category_id;
    const breadcrumbCategoryName = categoryName || entityData?.category_name;

    const navigate = useNavigate();

    // Shared styles
    const separatorCls = "text-muted-foreground/40 mx-0.5";
    const linkCls = "text-sm text-muted-foreground hover:text-foreground transition-colors truncate max-w-[180px]";
    const activeCls = "text-sm font-medium text-foreground truncate max-w-[220px]";

    return (
        <div className="flex items-center gap-2">
            <button
                onClick={() => navigate('/workspace')}
                className="inline-flex items-center justify-center -ml-1 h-7 w-7 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
                <Home className="h-3.5 w-3.5" />
                <span className="sr-only">Home</span>
            </button>
            <Separator orientation="vertical" className="h-3.5 opacity-50" />

            {title === "Notes" ? (
                <nav className="flex items-center gap-0.5 overflow-hidden">
                    <button
                        onClick={() => setSearchParams({})}
                        className={cn(linkCls, !currentFolderId && "text-foreground font-medium")}
                    >
                        Notes
                    </button>

                    {folderBreadcrumbs.map((folder) => (
                        <div key={folder.id} className="flex items-center gap-0.5">
                            <ChevronRight className={cn("h-3.5 w-3.5 flex-shrink-0", separatorCls)} />
                            <span className={cn(
                                folder.id === currentFolderId ? activeCls : linkCls
                            )}>
                                {folder.name}
                            </span>
                        </div>
                    ))}
                </nav>
            ) : showElementsBreadcrumbs ? (
                <nav className="flex items-center gap-0.5 overflow-hidden">
                    <Link
                        to={`/projects/${projectId}/elements`}
                        className={linkCls}
                    >
                        Elements
                    </Link>

                    {breadcrumbCategoryName && (
                        <>
                            <ChevronRight className={cn("h-3.5 w-3.5 flex-shrink-0", separatorCls)} />
                            <Link
                                to={`/projects/${projectId}/elements/${breadcrumbCategoryId}`}
                                className={cn(!entityId ? activeCls : linkCls)}
                            >
                                {breadcrumbCategoryName}
                            </Link>
                        </>
                    )}

                    {entityId && (entityName || entityData?.name) && (
                        <>
                            <ChevronRight className={cn("h-3.5 w-3.5 flex-shrink-0", separatorCls)} />
                            <span className={activeCls}>
                                {entityName || entityData?.name}
                            </span>
                        </>
                    )}
                </nav>
            ) : (
                <h1 className="text-sm font-medium text-foreground/90">
                    {title}
                </h1>
            )}
        </div>
    )
}
